/**
 * A ordem das etapas quando existe mais de um funil.
 *
 * ── Por que isto não é um `splice` na lista da tela ──────────────────────
 *
 * A tela de Configurações mostra UM funil por vez, mas `PUT /v1/funil/etapas`
 * ordena o campo `stage` do motor, que é a união de todos eles. As etapas de
 * funis diferentes ficam intercaladas ali — nada as agrupa —, e mandar só a
 * lista do funil visível faria as outras irem para o fim (ver `ordem` no
 * contrato: quem não é citado vai para o fim, na ordem em que estava).
 *
 * O efeito seria silencioso e feio: mexer na ordem de "Vendas" mudaria a
 * posição das etapas de "Oficina", e como a ordem dos FUNIS na lista segue a
 * primeira etapa de cada um, a revenda veria o seletor de funil se
 * reembaralhar sozinho.
 *
 * Por isso as duas operações trabalham sobre a ordem COMPLETA, mexendo só nas
 * posições que o funil visível ocupa.
 */

/**
 * Troca duas etapas de lugar na ordem completa.
 *
 * É a operação das setas. Trocar posições — em vez de remover e reinserir —
 * é o que mantém intacto tudo o que está entre elas, inclusive etapa de outro
 * funil que por acaso esteja no meio.
 */
export function ordemComTroca(todas: readonly string[], a: string, b: string): readonly string[] {
  const i = todas.indexOf(a);
  const j = todas.indexOf(b);
  if (i < 0 || j < 0) return todas;

  const ordem = [...todas];
  ordem[i] = b;
  ordem[j] = a;
  return ordem;
}

/**
 * Onde a etapa nova entra na ordem completa.
 *
 * `antesDe` é a etapa do MESMO funil que ela deve preceder; `null` é "no fim
 * do funil", que não é o fim da lista — é logo depois da última etapa deste
 * funil, para a nova não pular por cima das de outro.
 *
 * O valor novo é tirado da lista antes de ser reinserido: o motor acrescenta
 * no fim, então ele já chega aqui na última posição, e sem tirar ele apareceria
 * duas vezes.
 */
export function ordemComEtapaInserida(
  todas: readonly string[],
  valor: string,
  alvo: { readonly antesDe: string } | { readonly depoisDe: string } | null,
): readonly string[] {
  const ordem = todas.filter((v) => v !== valor);

  if (alvo === null) {
    ordem.push(valor);
    return ordem;
  }

  const referencia = 'antesDe' in alvo ? alvo.antesDe : alvo.depoisDe;
  const onde = ordem.indexOf(referencia);

  // Referência que sumiu — outra pessoa removeu a etapa entre a leitura e o
  // clique. O fim da lista é o destino honesto: a etapa existe, e as setas
  // consertam em dois cliques.
  if (onde < 0) {
    ordem.push(valor);
    return ordem;
  }

  ordem.splice('antesDe' in alvo ? onde : onde + 1, 0, valor);
  return ordem;
}
